import { useEffect, useMemo, useState } from 'react';

import { Collapse } from '@mui/material';
import { useFormState } from 'react-final-form';

import {
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlayProvider,
  DrawerProps,
  Icons,
  Spinner,
  useDrawerInstance,
  Typography,
  Stack,
} from '@cast/design-system';
import { EdgeConfiguration } from '@cast/types-omni';

import { CloseDrawerGuard } from 'components/drawer';
import { CUSTOM_EDGE_PROVIDER } from 'features/cluster/autoscaler/edge-locations/constants';
import {
  useCreateEdgeConfigurationMutation,
  useUpdateEdgeConfigurationMutation,
} from 'hooks/mutations/omni';
import { useGetClusterEdgeLocationsQuery } from 'hooks/queries/omni';
import { useGetEdgeConfigurationQuery } from 'hooks/queries/omni/useGetEdgeConfigurationQuery';

import { useEdgeConfigurationAbilities } from '../useEdgeConfigurationAbilities';
import {
  fromEdgeConfigurationApiModel,
  toEdgeConfigurationApiModel,
} from './utils';
import type { EdgeConfigurationFormValues } from './validate.schema';
import { validate } from './validate.schema';
import { EdgeConfigurationListItem } from '../../types';
import { BootDiskSection } from './_components/BootDiskSection';
import { CRISocketPathSection } from './_components/CRISocketPathSection';
import { EdgeLocationSection } from './_components/EdgeLocationSection';
import { ImageSection } from './_components/ImageSection';
import { InitScriptSection } from './_components/InitScriptSection';
import { NameSection } from './_components/NameSection';
import { NebiusGpuClusterSection } from './_components/NebiusGpuClusterSection';
import { NebiusReservationIdsSection } from './_components/NebiusReservationIdsSection';
import { SshKeySection } from './_components/SshKeySection';
import { TagsSection } from './_components/TagsSection';

import { useCluster } from '@cast/shared-cluster-context-data-access';

import { RffForm } from '@cast/shared-rff-ui';
import { Cloud } from '@cast/shared-types';
import { EnhancedEdgeLocation } from 'features/cluster/autoscaler/edge-locations/types';

type EdgeConfigurationDrawerProps = {
  edgeConfiguration?: EdgeConfigurationListItem;
} & DrawerProps;

const CancelButton = () => {
  const { close, setShouldRenderGuard } = useDrawerInstance();
  const { dirty } = useFormState({
    subscription: { dirty: true },
  });

  const handleCancel = () => {
    if (dirty) {
      setShouldRenderGuard(true);
    } else {
      close();
    }
  };

  return (
    <Button
      variant="secondary"
      onClick={handleCancel}
      testId="cancel-edge-configuration-button"
    >
      Cancel
    </Button>
  );
};

type DrawerMode = 'create' | 'edit' | 'view';

const NebiusFields = ({
  edgeLocations,
}: {
  edgeLocations: EnhancedEdgeLocation[];
}) => {
  const { values } = useFormState();
  const selectedEdgeLocation = useMemo(() => {
    return edgeLocations?.find(
      (edgeLocation) => edgeLocation.id === values?.edgeLocationId
    );
  }, [values, edgeLocations]);

  if (selectedEdgeLocation?.provider !== Cloud.nebius) {
    return null;
  }

  return (
    <>
      <NebiusGpuClusterSection />
      <NebiusReservationIdsSection />
    </>
  );
};

export const EdgeConfigurationDrawer = ({
  edgeConfiguration,
  ref,
  ...drawerProps
}: EdgeConfigurationDrawerProps) => {
  const { close } = useDrawerInstance();
  const { cluster } = useCluster();
  const { canUpdate, canCreate } = useEdgeConfigurationAbilities();
  const { id: configId, edgeLocationId } = edgeConfiguration || {};

  const isEditMode = !!configId;
  const isReadOnly = isEditMode ? !canUpdate : !canCreate;
  const mode: DrawerMode = !configId ? 'create' : isReadOnly ? 'view' : 'edit';

  const { mutateAsync: createEdgeConfiguration, isPending: isCreating } =
    useCreateEdgeConfigurationMutation();
  const { mutateAsync: updateEdgeConfiguration, isPending: isUpdating } =
    useUpdateEdgeConfigurationMutation({
      edgeConfigurationId: configId || '',
      edgeLocationId: edgeLocationId || '',
      clusterId: cluster.id,
    });
  const { edgeConfiguration: fetchedConfig, isLoading: isLoadingConfig } =
    useGetEdgeConfigurationQuery({
      clusterId: cluster.id,
      edgeLocationId,
      edgeConfigurationId: configId,
      enabled: isEditMode,
    });

  const { edgeLocations } = useGetClusterEdgeLocationsQuery({
    clusterId: cluster.id,
  });

  const [isAdvancedOpen, setIsAdvancedOpen] = useState(true);
  const [initScriptType, setInitScriptType] = useState<'yaml' | 'bash'>('bash');

  const isPending = isCreating || isUpdating;

  const edgeLocationOptions = useMemo(() => {
    if (!edgeLocations) {
      return [];
    }
    const locations = !configId
      ? edgeLocations.filter(
          (location) => location.provider !== CUSTOM_EDGE_PROVIDER
        )
      : edgeLocations;
    return locations.map((location) => ({
      id: location.id!,
      name: location.name || location.id!,
      provider: location.provider,
    }));
  }, [edgeLocations, configId]);

  const selectedEdgeLocation = useMemo(() => {
    if (!edgeLocationId || !edgeLocations) {
      return undefined;
    }
    return edgeLocations.find((l) => l.id === edgeLocationId);
  }, [edgeLocationId, edgeLocations]);

  const formInitialValues = useMemo<EdgeConfigurationFormValues>(() => {
    if (!isEditMode || !fetchedConfig) {
      return {
        edgeLocationId: '',
        name: '',
        tags: [{ key: '', value: '' }],
        bootDiskSizeGib: '',
        imageId: '',
        criSocketPath: '',
        default: false,
        initScript: '',
        sshPublicKey: '',
        gpuCluster: '',
        reservationIds: [],
      };
    }

    return fromEdgeConfigurationApiModel(fetchedConfig);
  }, [isEditMode, fetchedConfig]);

  useEffect(() => {
    if (formInitialValues?.initScript?.startsWith('#cloud-config')) {
      setInitScriptType('yaml');
    } else {
      setInitScriptType('bash');
    }
  }, [formInitialValues]);

  const handleSubmit = async (values: EdgeConfigurationFormValues) => {
    const selected = isEditMode
      ? selectedEdgeLocation
      : edgeLocations?.find((l) => l.id === values.edgeLocationId);

    const payload = toEdgeConfigurationApiModel(
      values,
      selected?.provider,
      isEditMode
    );

    if (isEditMode) {
      await updateEdgeConfiguration(payload);
    } else {
      await createEdgeConfiguration({
        clusterId: cluster.id,
        edgeLocationId: values.edgeLocationId,
        edgeConfiguration: payload as EdgeConfiguration,
      });
    }

    close();
  };

  return (
    <Drawer
      ref={ref}
      testId={`edge-configuration-${mode}-drawer`}
      size="xlarge"
      {...drawerProps}
    >
      <DrawerOverlayProvider>
        <DrawerHeader />
        <RffForm
          array
          mode={mode}
          sx={{ display: 'contents' }}
          onSubmit={handleSubmit}
          initialValues={formInitialValues}
          validate={isReadOnly ? undefined : validate}
          subscription={{ submitting: true, invalid: true }}
        >
          {({ submitting, invalid }) => (
            <>
              <CloseDrawerGuard />
              <DrawerBody height="100%">
                {isEditMode && isLoadingConfig ? (
                  <Stack
                    justifyContent="center"
                    alignItems="center"
                    height="100%"
                    display="flex"
                  >
                    <Spinner color="primary" size={40} />
                  </Stack>
                ) : (
                  <>
                    <Typography
                      variant="h5"
                      data-testid="drawer-title"
                      pt={10}
                      pb={isEditMode ? 0 : 32}
                      px={32}
                    >
                      {isEditMode
                        ? fetchedConfig?.name
                        : 'Create edge node configuration'}
                    </Typography>
                    {!isEditMode && <NameSection />}
                    <DrawerContent>
                      <Stack>
                        <EdgeLocationSection
                          isCreateMode={mode === 'create'}
                          edgeLocationOptions={edgeLocationOptions}
                          clusterId={cluster.id}
                        />
                        <TagsSection />
                        <BootDiskSection />
                        <ImageSection />
                        <CRISocketPathSection />
                        <SshKeySection />
                        <NebiusFields edgeLocations={edgeLocations || []} />
                        <Button
                          testId="advanced-settings-button"
                          variant="text"
                          endIcon={
                            isAdvancedOpen ? (
                              <Icons.CaretUp />
                            ) : (
                              <Icons.CaretDown />
                            )
                          }
                          sx={{ mt: 22 }}
                          onClick={() => setIsAdvancedOpen((v) => !v)}
                        >
                          <Typography variant="P14M" color="primary">
                            Advanced settings
                          </Typography>
                        </Button>

                        <Collapse in={isAdvancedOpen}>
                          <InitScriptSection
                            isReadOnly={isReadOnly}
                            initScriptType={initScriptType}
                            setInitScriptType={setInitScriptType}
                          />
                        </Collapse>
                      </Stack>
                    </DrawerContent>
                  </>
                )}
              </DrawerBody>

              <DrawerFooter>
                <CancelButton />
                <Button
                  type="submit"
                  variant="primary"
                  disabled={submitting || invalid || isPending || isReadOnly}
                  testId="save-edge-configuration-button"
                >
                  Save changes
                </Button>
              </DrawerFooter>
            </>
          )}
        </RffForm>
      </DrawerOverlayProvider>
    </Drawer>
  );
};
